/**
 * Gemeinsame Typen/Labels für das Fristen-Register (Techniker-Flyout + Kundenportal).
 */

/** Bezugspunkt der Kündigungsfrist: Laufzeitende oder Ende des Monats, in dem sie endet. */
export type DeadlineNoticeAnchor = 'END_DATE' | 'MONTH_END';

/**
 * Kündigungsfrist in Worten — „30 Tage zum Monatsende" statt einer nackten Zahl.
 * Die Formel ist der Grund, warum die Handlungsfrist steht, wo sie steht; ohne sie
 * wirkt das Datum willkürlich und wird angezweifelt.
 */
export function noticeLabel(
  noticeDays: number | null | undefined,
  anchor: DeadlineNoticeAnchor | null | undefined
): string | null {
  if (!noticeDays || noticeDays <= 0) return null;
  return anchor === 'MONTH_END'
    ? `Kündigungsfrist ${noticeDays} Tage zum Monatsende`
    : `Kündigungsfrist ${noticeDays} Tage`;
}

export type CustomerDeadlineCategory =
  | 'LICENSE' | 'SERVER' | 'HOSTING' | 'DOMAIN' | 'CERTIFICATE' | 'CONTRACT' | 'SUPPORT'
  | 'MOBILE' | 'INTERNET' | 'OTHER';

export type CustomerDeadlineOwnership = 'FIXBYTE' | 'CUSTOMER_MANAGED';

/**
 * FIXED = eigene Laufzeit. PENDING = eigene Laufzeit, das Ende ist angefragt (im Portal mit Hinweis).
 * SERVICE_CONTRACT = Managed Service, der mit dem FixByte-Servicevertrag läuft. Die beiden letzten
 * haben kein Laufzeitende, also weder Frist noch Ampel noch Erinnerung.
 */
export type CustomerDeadlineTermType = 'FIXED' | 'PENDING' | 'SERVICE_CONTRACT';

/**
 * RENEWING = verlängert sich automatisch (innerhalb 90 Tage) – kein Alarm, nur melden bei Änderungswunsch.
 * ENDING   = gekündigt, läuft gewollt aus – ebenfalls kein Alarm: hier wurde nichts
 *            versäumt, hier wurde entschieden.
 */
export type CustomerDeadlineStatus = 'EXPIRED' | 'CRITICAL' | 'WARNING' | 'RENEWING' | 'ENDING' | 'OK';

export type DeadlineRequestKind = 'CHANGE' | 'CANCEL' | 'RENEW' | 'QUESTION';

export const DEADLINE_REQUEST_KIND_LABELS: Record<DeadlineRequestKind, string> = {
  CHANGE: 'Änderung',
  CANCEL: 'Kündigung',
  RENEW: 'Verlängerung',
  QUESTION: 'Rückfrage',
};

export interface DeadlinePendingRequest {
  kind: DeadlineRequestKind | string;
  ticketId: string | null;
  ticketNumber: string | null;
  at: string | null;
}

export const DEADLINE_CATEGORY_LABELS: Record<CustomerDeadlineCategory, string> = {
  LICENSE: 'Lizenz',
  SERVER: 'Server',
  HOSTING: 'Hosting',
  DOMAIN: 'Domain',
  CERTIFICATE: 'Zertifikat',
  CONTRACT: 'Vertrag',
  SUPPORT: 'Support / Wartung',
  MOBILE: 'Mobilfunk',
  INTERNET: 'Internet / Telefonie',
  OTHER: 'Sonstiges',
};

export const DEADLINE_CATEGORY_ORDER: CustomerDeadlineCategory[] = [
  'LICENSE', 'SERVER', 'HOSTING', 'DOMAIN', 'CERTIFICATE', 'CONTRACT', 'SUPPORT', 'MOBILE', 'INTERNET', 'OTHER',
];

export const DEADLINE_OWNERSHIP_LABELS: Record<CustomerDeadlineOwnership, { short: string; long: string; color: string }> = {
  FIXBYTE: {
    short: 'von FixByte bereitgestellt',
    long: 'FixByte ist Vertragspartner und stellt den Posten im eigenen Namen für dich bereit',
    color: 'orange',
  },
  CUSTOMER_MANAGED: {
    short: 'dein Vertrag · von FixByte verwaltet',
    long: 'Du bist selbst Inhaber/Vertragspartner – FixByte verwaltet den Posten für dich',
    color: 'blue',
  },
};

export const DEADLINE_STATUS_META: Record<CustomerDeadlineStatus, { label: string; color: string }> = {
  EXPIRED: { label: 'Abgelaufen', color: 'red' },
  CRITICAL: { label: 'Handlung nötig', color: 'red' },
  WARNING: { label: 'Bald fällig', color: 'orange' },
  RENEWING: { label: 'Verlängert sich', color: 'blue' },
  ENDING: { label: 'Läuft aus', color: 'gray' },
  OK: { label: 'OK', color: 'teal' },
};

/** Braucht der Kunde selbst aktiv etwas tun? (nicht bei Auto-Verlängerung / OK) */
export function needsCustomerAction(status: CustomerDeadlineStatus): boolean {
  return status === 'EXPIRED' || status === 'CRITICAL' || status === 'WARNING';
}

/** Gemeinsame Felder, wie sie Portal- und Techniker-API liefern. */
/** Beleg (geschwärzter Screenshot) — im Kundenportal nur freigegebene, ohne Freigabe-Feld. */
export interface DeadlineProof {
  id: string;
  filename: string;
  mimeType: string;
  note: string | null;
  createdAt: string;
  sizeBytes?: number;
  visibleToCustomer?: boolean;
}

export interface CustomerDeadlineBase {
  /** Belege zum Posten */
  proofs?: DeadlineProof[];
  id: string;
  category: CustomerDeadlineCategory;
  ownership: CustomerDeadlineOwnership;
  name: string;
  provider: string | null;
  reference: string | null;
  /** YYYY-MM-DD */
  startDate: string | null;
  /** YYYY-MM-DD */
  endDate: string;
  /**
   * Ende der Periode, zu der die Handlungsfrist gehört (YYYY-MM-DD). Bei Auto-Verlängerung mit
   * Intervall rollt es nach verpasster Frist auf die nächste Periode — anzeigen statt `endDate`.
   */
  termEndDate: string;
  autoRenew: boolean;
  renewalMonths: number | null;
  noticeDays: number | null;
  noticeAnchor: DeadlineNoticeAnchor;
  /** true = gekündigt, läuft gewollt aus — kein Handlungsbedarf */
  terminationPlanned?: boolean;
  /** Gesellschaft des Postens — in der Konzern-Gesamtsicht gesetzt, sonst null */
  companyName?: string | null;
  customerId?: string;
  customerNote: string | null;
  isActive: boolean;
  /** Offene Kundenanfrage aus dem Portal (bis Techniker sie erledigt) */
  pendingRequest?: DeadlinePendingRequest | null;
  /** Vom Kunden selbst im Portal erfasst (Eigen-Tracking) */
  createdByCustomer?: boolean;
  /** Kunde darf diesen Eintrag bearbeiten/löschen (nur eigene Einträge) */
  canEdit?: boolean;
  /** Handlungsfrist YYYY-MM-DD (Kündigungsfrist oder Laufzeitende) */
  actionDate: string;
  daysLeft: number;
  status: CustomerDeadlineStatus;
  actionIsCancellation: boolean;
}

/**
 * Handlungsfrist wie im Backend (`actionDateOf` in customerDeadlineStatus.ts) — für die Vorschau
 * beim Erfassen. Mit Kündigungsfrist bei automatischer Verlängerung: Laufzeitende bzw. dessen
 * Monatsende minus Frist; sonst das Laufzeitende selbst.
 */
export function actionDateOfIso(
  endIso: string,
  autoRenew: boolean,
  noticeDays: number | null,
  anchor: DeadlineNoticeAnchor = 'END_DATE'
): string {
  if (!autoRenew || !noticeDays || noticeDays <= 0) return endIso;
  const [y, m, d] = endIso.split('-').map(Number);
  const base = anchor === 'MONTH_END' ? Date.UTC(y, m, 0) : Date.UTC(y, m - 1, d);
  return new Date(base - noticeDays * 86_400_000).toISOString().slice(0, 10);
}

/** Schutz vor Endlosschleifen bei unsinnigen Daten — dieselbe Obergrenze wie `MAX_ROLLED_TERMS` im Backend. */
const MAX_ROLLED_TERMS = 1200;

/**
 * Monate addieren wie `addUtcMonths` im Backend: Ein Monatsletzter bleibt Monatsletzter
 * (31.01. + 1 → 28.02. bzw. 29.02., 28.02.2027 + 12 → 29.02.2028), sonst wird aufs Monatsende gekappt.
 */
export function addMonthsIso(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const wasMonthEnd = d === new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lastDay = new Date(Date.UTC(y, m + months, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, wasMonthEnd ? lastDay : Math.min(d, lastDay))).toISOString().slice(0, 10);
}

/**
 * Laufzeitende der Periode, um die es gerade geht — wie `currentTermEnd` im Backend, aus dem die Liste
 * `termEndDate` bekommt. Bei Auto-Verlängerung mit Intervall wird fortgeschrieben, bis die Handlungsfrist
 * heute oder später liegt; ohne Auto-Verlängerung, ohne Intervall oder gekündigt bleibt es beim Datum.
 * `todayIso` ist wie im Backend der UTC-Tag.
 */
export function currentTermEndIso(
  d: {
    endDate: string;
    autoRenew: boolean;
    renewalMonths: number | null;
    noticeDays: number | null;
    noticeAnchor?: DeadlineNoticeAnchor;
    terminationPlanned?: boolean;
  },
  todayIso: string = new Date().toISOString().slice(0, 10)
): string {
  const months = d.renewalMonths ?? 0;
  if (!d.autoRenew || d.terminationPlanned || months <= 0) return d.endDate;
  let end = d.endDate;
  // YYYY-MM-DD ist als String sortierbar.
  for (let i = 0; i < MAX_ROLLED_TERMS && actionDateOfIso(end, true, d.noticeDays, d.noticeAnchor) < todayIso; i++) {
    end = addMonthsIso(end, months);
  }
  return end;
}

/** "2026-05-12" → "12.05.2026" (reine String-Formatierung). */
export function formatIsoDateDe(iso: string | null | undefined): string {
  if (!iso) return '–';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** Menschlich lesbare Restzeit: „in 12 Tagen", „heute", „vor 3 Tagen". */
export function formatDaysLeft(days: number): string {
  if (days === 0) return 'heute';
  if (days === 1) return 'morgen';
  if (days === -1) return 'gestern';
  if (days < 0) return `vor ${Math.abs(days)} Tagen`;
  if (days >= 60) {
    const months = Math.round(days / 30);
    return `in ca. ${months} Monaten`;
  }
  return `in ${days} Tagen`;
}
